'use client';

import { onlineOrderingProductImage } from '@trashlab/api/src/domains/pricing/shared/product-illustration';

import {
  Box,
  Text,
  Card,
  Flex,
  Select,
  Callout,
  Spinner,
} from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import { AddressPicker } from '@trashlab/sitesapp/src/organizations/redebins/components/shared/address-picker';
import {
  trpcConsumerSubmitInformation,
  trpcConsumerListPricing,
  trpcConsumerUpdateCartSku,
  trpcConsumerGetWebsiteCart,
  trpcConsumerGetChargesForCart,
} from '@trashlab/api/src/generated/hooks';
import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types';
import { SummarySectionCard } from '@trashlab/sitesapp/src/organizations/redebins/components/shop/summary-section-card';
import {
  IconShopLocation,
  IconShopNotes,
  IconShopMaterial,
  IconHomeFilled,
  IconStore,
} from '@trashlab/sitesapp/src/organizations/redebins/template/components/icons/icons';
import validations from '@trashlab/api/src/domains/consumer/validations/sitesapp/index';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShopStepFrame } from '@trashlab/sitesapp/src/organizations/redebins/components/shop/shop-step-frame';
import { expectIntoValue } from '@trashlab/api/src/shared/utils';
import { Address } from '@trashlab/api/src/domains/core/types';
import { FormInput } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/form-input';
import * as Form from '@radix-ui/react-form';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { useTrpcInvalidate } from '@trashlab/viteapp/src/hooks/use-trpc-invalidate';
import currency from 'currency.js';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconCheck,
  IconCalendar,
} from '@tabler/icons-react';
import { Button } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/button';
import StorageImage from '@trashlab/sitesapp/src/organizations/redebins/components/shared/storage-image';
import { useRef } from 'react';
import { usePortalContainer } from '@trashlab/sitesapp/src/organizations/redebins/contexts/portal-container-context';

export const InformationStepSummary = ({
  cartData,
}: {
  cartData: TLSiteCartData;
}) => {
  const listPricingQuery = trpcConsumerListPricing.useQuery({
    cartId: cartData.cart.id,
    tenantId: cartData.cart.tenantId,
  });

  if (!cartData.cart.siteAddress) {
    return null;
  }

  const { materials } = listPricingQuery.data
    ? expectIntoValue(listPricingQuery.data)
    : { materials: [] };

  const material = materials.find((m) => m.id === cartData.cart.materialId);

  return (
    <>
      <SummarySectionCard
        icon={IconShopLocation}
        label="Dumpster drop off location"
      >
        <Text>
          {cartData.cart.siteAddress.streetAddressPartOne}
          {cartData.cart.siteAddress.streetAddressPartTwo
            ? ` ${cartData.cart.siteAddress.streetAddressPartTwo}`
            : ''}
          , {cartData.cart.siteAddress.city}, {cartData.cart.siteAddress.state}{' '}
          {cartData.cart.siteAddress.postalCode}
        </Text>
      </SummarySectionCard>

      <SummarySectionCard icon={IconShopMaterial} label="Material type">
        <Text>{material?.name}</Text>
      </SummarySectionCard>

      {cartData.cart.customerNotes && (
        <SummarySectionCard icon={IconShopNotes} label="Placement Instructions">
          <Text>{cartData.cart.customerNotes}</Text>
        </SummarySectionCard>
      )}
    </>
  );
};

type DumpsterOption = {
  skuId: string;
  productId: string;
  name: string;
  price: string;
  cubicYardage: string;
  dumpsterImage?: string;
  daysLimit?: number;
};

export const InformationStep = ({ cartData }: { cartData: TLSiteCartData }) => {
  const requirePlacementInstructions =
    cartData.cart.shop?.requirePlacementInstructions ?? false;
  const portalContainer = usePortalContainer();

  const submitMutation = trpcConsumerSubmitInformation.useMutation();
  const updateSkuMutation = trpcConsumerUpdateCartSku.useMutation();
  const invalidate = useTrpcInvalidate();

  // Ref to track if we're currently processing a SKU update to prevent infinite loops
  const isUpdatingSkuRef = useRef(false);

  const [addressStatus, setAddressStatus] = useState<
    'idle' | 'checking' | 'available' | 'unavailable'
  >('idle');
  const [availableDumpsterOptions, setAvailableDumpsterOptions] = useState<
    DumpsterOption[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<DumpsterOption | null>(
    null,
  );

  const form = useForm({
    defaultValues: {
      siteAddress: (cartData.cart.siteAddress || {
        streetAddressPartOne: '',
        streetAddressPartTwo: '',
        city: '',
        state: '',
        zip: '',
        latitude: 0,
        longitude: 0,
      }) as Address,
      containerRotation: cartData.cart.containerRotation ?? 0,
      materialId: cartData.cart.materialId ?? '',
      wasteGeneratorCategory:
        cartData.cart.wasteGeneratorCategory ?? 'RESIDENTIAL',
      customerNotes: cartData.cart.customerNotes ?? '',
      attachments:
        cartData.cart.containerLocationPhotos?.map((attachment) => ({
          fileId: attachment.photo.id,
          url: attachment.photo.url ?? '',
          type: attachment.photo.fileType ?? '',
          notes: attachment.photo.notes ?? '',
        })) ?? [],
      cartId: cartData.cart.id,
      tenantId: cartData.cart.tenantId,
    },
    resolver: zodResolver(validations.submitInformation),
    mode: 'onChange',
  });

  const siteAddress = form.watch('siteAddress');
  const materialId = form.watch('materialId');
  const wasteGeneratorCategory = form.watch('wasteGeneratorCategory');
  const customerNotes = form.watch('customerNotes');

  const isAddressValid =
    siteAddress?.streetAddressPartOne?.trim() !== '' &&
    siteAddress?.city?.trim() !== '' &&
    siteAddress?.state?.trim() !== '' &&
    siteAddress?.postalCode?.trim() !== '' &&
    !!siteAddress?.latitude &&
    !!siteAddress?.longitude;

  const listPricingQuery = trpcConsumerListPricing.useQuery(
    {
      cartId: cartData.cart.id,
      tenantId: cartData.cart.tenantId,
      address: {
        postalCode: siteAddress?.postalCode,
        latitude: siteAddress?.latitude,
        longitude: siteAddress?.longitude,
      },
    },
    {
      enabled: isAddressValid,
    },
  );

  const handleSkuUpdateSuccess = async () => {
    isUpdatingSkuRef.current = false;
    await invalidate(trpcConsumerGetWebsiteCart);
    await invalidate(trpcConsumerGetChargesForCart);
    setAddressStatus('available');
  };

  const handleSkuUpdateError = () => {
    isUpdatingSkuRef.current = false;
    setAddressStatus('unavailable');
  };

  useEffect(() => {
    if (!isAddressValid) {
      setAddressStatus('idle');
      return;
    }

    setAddressStatus('checking');

    if (listPricingQuery.isSuccess && listPricingQuery.data) {
      const originalProductId = cartData.cart.productId;
      const originalSkuId = cartData.cart.skuId;
      const originalCubicYardage = cartData.cart.product?.cubicYardage;

      if (!originalProductId || !originalSkuId || !originalCubicYardage) {
        setAddressStatus('unavailable');
        return;
      }

      const availablePriceCards = listPricingQuery.data.priceCards;

      if (availablePriceCards.length === 0) {
        setAddressStatus('unavailable');
        return;
      }

      const exactProductCard = availablePriceCards.find((pc) =>
        pc.products.some((p) => p.productId === originalProductId),
      );

      if (exactProductCard) {
        const locationSpecificSkuId = exactProductCard.id;
        if (
          locationSpecificSkuId !== originalSkuId &&
          !isUpdatingSkuRef.current
        ) {
          isUpdatingSkuRef.current = true;
          updateSkuMutation.mutate(
            {
              cartId: cartData.cart.id,
              tenantId: cartData.cart.tenantId,
              skuId: locationSpecificSkuId,
              productId:
                exactProductCard.products[0]?.productId || originalProductId,
            },
            {
              onSuccess: handleSkuUpdateSuccess,
              onError: handleSkuUpdateError,
            },
          );
        } else if (!isUpdatingSkuRef.current) {
          setAddressStatus('available');
        }
      } else {
        const sameSizeProductCard = availablePriceCards.find((pc) =>
          pc.products.some(
            (p) => p.product?.cubicYardage === originalCubicYardage,
          ),
        );

        if (sameSizeProductCard) {
          const sameSizeSkuId = sameSizeProductCard.id;
          if (sameSizeSkuId !== originalSkuId && !isUpdatingSkuRef.current) {
            isUpdatingSkuRef.current = true;
            updateSkuMutation.mutate(
              {
                cartId: cartData.cart.id,
                tenantId: cartData.cart.tenantId,
                skuId: sameSizeSkuId,
                productId:
                  sameSizeProductCard.products[0]?.productId ||
                  originalProductId,
              },
              {
                onSuccess: handleSkuUpdateSuccess,
                onError: handleSkuUpdateError,
              },
            );
          } else if (!isUpdatingSkuRef.current) {
            setAddressStatus('available');
          }
        } else {
          // Prepare alternative options
          if (availablePriceCards.length > 0) {
            const options: DumpsterOption[] = availablePriceCards.map((pc) => {
              const product = pc.products[0]?.product;
              const cubicYardage = product?.cubicYardage;

              const price = pc.dumpsterPrice?.chargeFee
                ? currency(pc.dumpsterPrice.chargeFee, {
                    fromCents: true,
                  }).format()
                : 'Price unavailable';

              const dumpsterImage =
                onlineOrderingProductImage(product) ?? undefined;
              const daysLimit = pc.daysLimit ?? undefined;

              return {
                skuId: pc.id,
                productId: pc.products[0]?.productId || '',
                name: `${cubicYardage} - ${price}`,
                price,
                cubicYardage,
                dumpsterImage,
                daysLimit,
              };
            });

            setAvailableDumpsterOptions(options);
            setAddressStatus('unavailable');
          } else {
            setAddressStatus('unavailable');
          }
        }
      }
    }
  }, [
    isAddressValid,
    listPricingQuery.isSuccess,
    listPricingQuery.data,
    cartData.cart.productId,
    cartData.cart.skuId,
    cartData.cart.product?.cubicYardage,
    cartData.cart.id,
    cartData.cart.tenantId,
  ]);

  const isWasteGeneratorValid =
    siteAddress.state !== 'MN' || !!wasteGeneratorCategory;

  const { materials } = listPricingQuery.data
    ? expectIntoValue(listPricingQuery.data)
    : { materials: [] };

  const currentSkuIdForMaterialFiltering = cartData.cart.skuId;

  const filteredMaterials = materials.filter((material) => {
    if (!cartData.cart.productId || !currentSkuIdForMaterialFiltering)
      return true;

    const priceCard = listPricingQuery.data?.priceCards.find(
      (pc) => pc.id === currentSkuIdForMaterialFiltering,
    );
    if (!priceCard) return true;

    return priceCard.materials.some((m) => m.materialId === material.id);
  });

  const onSubmit = async () => {
    const values = form.getValues();
    await submitMutation.mutateAsync({
      ...validations.submitInformation.parse(values),
      tenantId: cartData.cart.tenantId,
    });
  };

  const isCurrentSkuProductAvailable = listPricingQuery.data?.priceCards.some(
    (pc) => pc.id === cartData.cart.skuId,
  );

  const renderAddressStatusIndicator = () => {
    if (addressStatus === 'checking') {
      return <Spinner className="w-6 h-6" />;
    }

    if (addressStatus === 'available') {
      return (
        <IconCircleCheck className="w-6 h-6 text-[var(--success-color)]" />
      );
    }

    if (addressStatus === 'unavailable') {
      return <IconAlertCircle className="w-6 h-6 text-[var(--danger-color)]" />;
    }

    return null;
  };

  return (
    <>
      <ShopStepFrame
        cartId={cartData.cart.id}
        onNext={onSubmit}
        canContinue={
          isAddressValid &&
          !!materialId &&
          isWasteGeneratorValid &&
          addressStatus === 'available' &&
          (!requirePlacementInstructions || !!customerNotes?.trim())
        }
        isLoading={
          submitMutation.isLoading ||
          updateSkuMutation.isLoading ||
          // Only consider listPricingQuery loading when query is actually enabled
          (isAddressValid && listPricingQuery.isLoading)
        }
        hideBackButton
      >
        <Form.Root onSubmit={onSubmit} className="w-full">
          <Flex direction="column" gap="2" my="2">
            {cartData.cart.shop?.shopCustomNotice && (
              <Callout.Root>
                <Callout.Icon>
                  <InfoCircledIcon className="w-4 h-4 font-bold" />
                </Callout.Icon>
                <Callout.Text weight="bold">
                  {cartData.cart.shop?.shopCustomNotice}
                </Callout.Text>
              </Callout.Root>
            )}

            <Text size="4" weight="bold">
              Delivery location
            </Text>

            <Box className="relative" style={{ zIndex: 20 }}>
              <AddressPicker
                id="siteAddress"
                prefix="siteAddress"
                customInitLabel="Dumpster drop off location"
                form={form}
                onRotationChange={(rotation: number) => {
                  form.setValue('containerRotation', Math.round(rotation));
                }}
                onAddressSelected={(placeId: string) => {
                  setAddressStatus('checking');
                }}
                rightIcon={renderAddressStatusIndicator()}
                errorMessage={
                  addressStatus === 'unavailable'
                    ? 'No dumpster size available for this address. Pick another option or address'
                    : undefined
                }
                addressStatus={addressStatus}
              />
              {isAddressValid &&
                addressStatus === 'unavailable' &&
                availableDumpsterOptions.length > 0 && (
                  <Flex direction="column" gap="3" mt="2">
                    {availableDumpsterOptions.map((option) => (
                      <Card
                        key={option.skuId}
                        className={`shadow-md p-2 cursor-pointer no-animate ${
                          selectedOption?.skuId === option.skuId
                            ? 'border-2 rounded-xl border-[var(--primary-color)] bg-[var(--accent-1)]'
                            : 'transform transition-all hover:scale-[1.01]'
                        }`}
                        onClick={() => setSelectedOption(option)}
                      >
                        <Flex gap="4" align="center" wrap="wrap">
                          {option.dumpsterImage && (
                            <Box className="w-[100px] rounded-md overflow-hidden">
                              <StorageImage
                                alt="Dumpster image"
                                bucket="container-product-images"
                                logoPath={option.dumpsterImage}
                                fit="contain"
                                width={'100%'}
                                height={'100%'}
                              />
                            </Box>
                          )}
                          <Flex
                            direction="column"
                            className="flex-1 min-w-[150px]"
                          >
                            {/* Use container name from settings */}
                            <Text size="2">{option.cubicYardage}</Text>
                            <Text size="2" weight="medium">
                              {option.price}
                            </Text>
                          </Flex>
                          {option.daysLimit && option.daysLimit > 0 && (
                            <Flex align="center" gap="2">
                              <Box className="p-1 rounded-md bg-blue-950/5">
                                <IconCalendar size={16} />
                              </Box>
                              <Text size="2">
                                {option.daysLimit} day rental
                              </Text>
                            </Flex>
                          )}

                          <Box
                            className={`p-2 rounded-full flex items-center justify-center w-6 h-6 ${
                              selectedOption?.skuId === option.skuId
                                ? 'bg-[var(--primary-color)]'
                                : 'border border-gray-300'
                            }`}
                          >
                            {selectedOption?.skuId === option.skuId && (
                              <IconCheck className="w-6 h-6 text-white" />
                            )}
                          </Box>
                        </Flex>
                      </Card>
                    ))}

                    <Button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!selectedOption) return;

                        await updateSkuMutation.mutateAsync(
                          {
                            cartId: cartData.cart.id,
                            tenantId: cartData.cart.tenantId,
                            skuId: selectedOption.skuId,
                            productId: selectedOption.productId,
                          },
                          {
                            onSuccess: handleSkuUpdateSuccess,
                            onError: () => setAddressStatus('unavailable'),
                          },
                        );
                      }}
                      disabled={!selectedOption || updateSkuMutation.isLoading}
                      loading={updateSkuMutation.isLoading}
                    >
                      Confirm address & dumpster
                    </Button>
                  </Flex>
                )}
            </Box>

            <Text size="4" weight="bold">
              Material type
            </Text>

            <Box className="relative w-full">
              <Select.Root
                value={materialId}
                name="materialId"
                onValueChange={(value) => form.setValue('materialId', value)}
                disabled={
                  !isAddressValid ||
                  listPricingQuery.isLoading ||
                  !isCurrentSkuProductAvailable ||
                  addressStatus !== 'available'
                }
              >
                <Select.Trigger
                  className="w-full h-[46px] bg-white rounded-md pointer-events-auto relative"
                  placeholder="Select material type"
                  id="materialId"
                  name="materialId"
                  data-field-name="materialId"
                  data-field-type="select-one"
                />
                <Select.Content
                  className="w-full min-w-[var(--radix-select-trigger-width)]"
                  // Keep the select portal inside the themed app container to avoid
                  // body-level portal teardown issues inside the fullscreen mobile cart.
                  container={portalContainer}
                  position="popper"
                  sideOffset={4}
                >
                  {filteredMaterials.map((material) => (
                    <Select.Item
                      key={material.id}
                      value={material.id}
                      id={`material-${material.id}`}
                    >
                      {material.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            <FormInput
              {...form.register('customerNotes', {
                ...(requirePlacementInstructions && {
                  required: 'Placement instructions are required',
                }),
              })}
              name="customerNotes"
              id="customerNotes"
              label="Placement Instructions"
              placeholder="Type here..."
              multiline
              optional={!requirePlacementInstructions}
              error={form.formState.errors.customerNotes?.message as string}
              data-field-name="customerNotes"
              data-field-type="text"
            />

            {siteAddress.state === 'MN' && (
              <>
                <Text size="4" weight="bold">
                  Select what type of waste is being generated
                </Text>
                {/* flex-col on mobile, row on sm+ screens to wrap cards properly */}
                <Flex className="w-full flex-col sm:flex-row" gap="4">
                  {[
                    {
                      title: 'Residential',
                      value: 'RESIDENTIAL',
                      icon: IconHomeFilled,
                      id: 'waste-type-residential',
                    },
                    {
                      title: 'Commercial',
                      value: 'COMMERCIAL',
                      icon: IconStore,
                      id: 'waste-type-commercial',
                    },
                  ].map((item) => {
                    const isSelected =
                      form.watch('wasteGeneratorCategory') === item.value;
                    return (
                      <Card
                        key={item.value}
                        id={item.id}
                        className={`
                          shadow-sm p-4 m-2 cursor-pointer flex-1 min-w-0
                          transition-all duration-100
                          hover:shadow-md hover:transform hover:scale-[1.01]
                          ${
                            isSelected
                              ? 'border-2 border-[var(--primary-color)]'
                              : 'border border-gray-100'
                          }
                        `}
                        onClick={() =>
                          form.setValue(
                            'wasteGeneratorCategory',
                            item.value as 'RESIDENTIAL' | 'COMMERCIAL',
                          )
                        }
                      >
                        <Flex gap="3" align="center" justify="between">
                          <Flex gap="3" align="center">
                            <Box className="w-12 h-12 rounded-md bg-[var(--accent-3)] flex items-center justify-center flex-shrink-0">
                              <item.icon className="w-6 h-6 text-[var(--primary-color)]" />
                            </Box>

                            <Text weight="medium" size="3">
                              {item.title}
                            </Text>
                          </Flex>

                          <Box className="flex-shrink-0">
                            <Flex
                              justify="center"
                              align="center"
                              className={`
                                w-6 h-6 rounded-md border
                                ${
                                  isSelected
                                    ? 'bg-[var(--primary-color)] border-[var(--primary-color)]'
                                    : 'border-gray-300'
                                }
                              `}
                              onClick={(e) => {
                                e.stopPropagation();
                                form.setValue(
                                  'wasteGeneratorCategory',
                                  item.value as 'RESIDENTIAL' | 'COMMERCIAL',
                                );
                              }}
                            >
                              {isSelected && (
                                <Box className="text-white">
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M5 12L10 17L19 8"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Box>
                              )}
                            </Flex>
                          </Box>
                        </Flex>
                      </Card>
                    );
                  })}
                </Flex>
              </>
            )}
          </Flex>
        </Form.Root>
      </ShopStepFrame>
    </>
  );
};
